// `/app/transactions/$id` (`spec/17-frontend.md` §8.1). Foco: **valor e status**.
//
// Desde WEB-50 este componente é o CONTEÚDO do `RouteDrawer` da rota-pai: o cabeçalho
// (título, subtítulo e o botão de fechar) é do drawer, e a lista continua desenhada
// embaixo, inclusive em deep link.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { isApiError } from '../api/errors.ts';
import { transactionOptions, transactionTimelineOptions } from '../api/queries/transactions.ts';
import { useMode } from '../app/mode.tsx';
import { EmptyState } from '../components/app/empty-state.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { SearchX } from '../components/icons/index.tsx';
import { TransactionDetailView } from '../features/transactions/transaction-detail.tsx';

/** Intervalo de repolling enquanto a cobrança está `pending`. */
const PENDING_POLL_MS = 5_000;

function TransactionPage() {
  const { id } = Route.useParams();
  const { mode } = useMode();

  // Enquanto a cobrança está `pending` a tela repolla a cada 5 s: o SSE do shell não é
  // alcançável pelo navegador até `FIX-CORS-SSE` subir, e quem acabou de criar a cobrança
  // fica olhando esta tela esperando o "Pago". `false` depois de um estado terminal para a
  // aba parada não gerar request para sempre.
  const pendingPoll = (status: string | undefined): number | false =>
    status === 'pending' ? PENDING_POLL_MS : false;

  const detail = useQuery({
    ...transactionOptions(mode, id),
    refetchInterval: (query) => pendingPoll(query.state.data?.status),
  });
  // A timeline só é buscada quando a cobrança existe: sem isso um 404 renderizaria dois
  // blocos de erro para a mesma causa. Ela acompanha o mesmo polling, senão o selo diria
  // "Pago" com a linha do tempo ainda parada em "criada".
  const timeline = useQuery({
    ...transactionTimelineOptions(mode, id, detail.isSuccess),
    refetchInterval: () => pendingPoll(detail.data?.status),
  });

  // Os dois timers de 5 s são independentes, e o do detalhe é o primeiro a ver o `paid`:
  // no mesmo render em que o selo vira "Pago", o `refetchInterval` da timeline passa a
  // `false` e o timer dela é cancelado ANTES de disparar. Sem este refetch a linha do
  // tempo ficaria parada na foto de até 5 s antes, justamente a janela em que a
  // transição, o lançamento no ledger e o outbox entraram (tudo no mesmo commit).
  const status = detail.data?.status;
  const refetchTimeline = timeline.refetch;
  useEffect(() => {
    if (status !== undefined && status !== 'pending') {
      void refetchTimeline();
    }
  }, [status, refetchTimeline]);

  if (detail.isPending) {
    return <CardSkeleton lines={5} />;
  }

  // 404 pelo `code`, nunca pelo texto do status (`spec/17` §4.3 item 1).
  if (isApiError(detail.error) && detail.error.code === 'resource_not_found') {
    return (
      <EmptyState
        bare
        icon={SearchX}
        title="Cobrança não encontrada"
        description="Este id não existe neste modo (live/teste) ou não é desta loja."
      />
    );
  }

  if (detail.error !== null || detail.data === undefined) {
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  return (
    <TransactionDetailView
      mode={mode}
      transaction={detail.data}
      timeline={{
        items: timeline.data?.data,
        isPending: timeline.isPending,
        error: timeline.error,
        onRetry: () => {
          void timeline.refetch();
        },
      }}
    />
  );
}

export const Route = createFileRoute('/app/transactions/$id')({ component: TransactionPage });
